import React, { useState, useEffect, useCallback } from "react";
import {
    Modal, ModalHeader, ModalBody,
    Badge, Table
} from "reactstrap";
import automationLogService from "../../../../services/automation_log_service";
import { useTenant } from "../../../../hooks/useTenant";
import { ContentLoader } from "../../../../components/Common";

/**
 * Mapeia status → cor + ícone para exibição visual.
 */
const STATUS_MAP = {
    QUEUED: { label: "Na fila", color: "secondary", icon: "mdi-clock-outline" },
    SENT: { label: "Enviado", color: "info", icon: "mdi-send-check" },
    DELIVERED: { label: "Entregue", color: "primary", icon: "mdi-check-all" },
    READ: { label: "Lido", color: "success", icon: "mdi-eye-check-outline" },
    FAILED: { label: "Falhou", color: "danger", icon: "mdi-alert-circle-outline" },
};

/**
 * Formata uma data para exibição amigável.
 */
const formatDateTime = (date) => {
    if (!date) return "—";
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit"
    });
};

/**
 * AutomationLogModal
 * Modal que exibe o histórico de envios de um gatilho de automação.
 */
const AutomationLogModal = ({ isOpen, toggle, triggerId, triggerLabel }) => {
    const { idTenant } = useTenant();
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedLogId, setExpandedLogId] = useState(null);

    const loadLogs = useCallback(async () => {
        if (!idTenant || !triggerId) return;
        setLoading(true);
        try {
            const [logsData, statsData] = await Promise.all([
                automationLogService.getLogsByTrigger(idTenant, triggerId, 100),
                automationLogService.getStatsByTrigger(idTenant, triggerId),
            ]);
            setLogs(logsData);
            setStats(statsData);
        } catch (err) {
            console.error("Erro ao carregar logs:", err);
        } finally {
            setLoading(false);
        }
    }, [idTenant, triggerId]);

    useEffect(() => {
        if (isOpen) {
            loadLogs();
            setExpandedLogId(null);
        }
    }, [isOpen, loadLogs]);

    const toggleExpand = (logId) => {
        setExpandedLogId(prev => prev === logId ? null : logId);
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
            <ModalHeader toggle={toggle} className="border-bottom">
                <div className="d-flex align-items-center gap-2">
                    <i className="mdi mdi-history font-size-20 text-primary"></i>
                    <div>
                        <span className="fw-bold">Histórico de Envios</span>
                        <small className="d-block text-muted">{triggerLabel}</small>
                    </div>
                </div>
            </ModalHeader>
            <ModalBody className="p-0">
                {/* Cards de estatísticas */}
                {stats && (
                    <div className="d-flex gap-2 p-3 border-bottom bg-light flex-wrap">
                        <StatCard label="Total" value={stats.total} icon="mdi-email-outline" color="#6c757d" />
                        <StatCard label="Enviados" value={stats.sent} icon="mdi-send-check" color="#0dcaf0" />
                        <StatCard label="Entregues" value={stats.delivered} icon="mdi-check-all" color="#0d6efd" />
                        <StatCard label="Lidos" value={stats.read} icon="mdi-eye-check-outline" color="#198754" />
                        <StatCard label="Falhas" value={stats.failed} icon="mdi-alert-circle-outline" color="#dc3545" />
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <ContentLoader message="Carregando histórico..." />
                )}

                {/* Sem logs */}
                {!loading && logs.length === 0 && (
                    <div className="text-center py-5">
                        <i className="mdi mdi-email-off-outline font-size-48 text-muted opacity-25 d-block mb-3"></i>
                        <p className="text-muted mb-1">Nenhum envio registrado</p>
                        <small className="text-muted">
                            Os logs aparecerão quando as Cloud Functions estiverem ativas.
                        </small>
                    </div>
                )}

                {/* Tabela de logs */}
                {!loading && logs.length > 0 && (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <Table hover responsive className="mb-0 align-middle">
                            <thead className="bg-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr>
                                    <th className="font-size-12 fw-bold text-muted py-2 ps-3">Destinatário</th>
                                    <th className="font-size-12 fw-bold text-muted py-2">Canal</th>
                                    <th className="font-size-12 fw-bold text-muted py-2">Status</th>
                                    <th className="font-size-12 fw-bold text-muted py-2">Data</th>
                                    <th className="font-size-12 fw-bold text-muted py-2 pe-3" style={{ width: '40px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <React.Fragment key={log.id}>
                                        <tr
                                            onClick={() => toggleExpand(log.id)}
                                            style={{ cursor: 'pointer' }}
                                            className={expandedLogId === log.id ? 'table-active' : ''}
                                        >
                                            <td className="ps-3">
                                                <div>
                                                    <span className="fw-semibold font-size-13">{log.recipientName}</span>
                                                    <small className="d-block text-muted">
                                                        {log.recipientType === "employee" ? (
                                                            <><i className="mdi mdi-teach me-1"></i>Professor</>
                                                        ) : (
                                                            <><i className="mdi mdi-account-outline me-1"></i>Aluno</>
                                                        )}
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                <ChannelBadge channel={log.channel} />
                                            </td>
                                            <td>
                                                <StatusBadge status={log.status} />
                                            </td>
                                            <td>
                                                <small className="text-muted">{formatDateTime(log.createdAt)}</small>
                                            </td>
                                            <td className="pe-3">
                                                <i className={`mdi ${expandedLogId === log.id ? 'mdi-chevron-up' : 'mdi-chevron-down'} font-size-16 text-muted`}></i>
                                            </td>
                                        </tr>

                                        {/* Detalhes expandidos */}
                                        {expandedLogId === log.id && (
                                            <tr>
                                                <td colSpan="5" className="p-0">
                                                    <div className="bg-light p-3 border-top border-bottom">
                                                        <div className="row g-3">
                                                            {/* Mensagem */}
                                                            <div className="col-12">
                                                                <label className="fw-bold font-size-11 text-muted text-uppercase mb-1 d-block">
                                                                    Mensagem Enviada
                                                                </label>
                                                                <div
                                                                    className="bg-white rounded-0 p-3 font-size-12 border"
                                                                    style={{ whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto' }}
                                                                >
                                                                    {log.messageFull || log.messagePreview || "—"}
                                                                </div>
                                                            </div>

                                                            {/* Detalhes do contato */}
                                                            <div className="col-md-6">
                                                                <label className="fw-bold font-size-11 text-muted text-uppercase mb-1 d-block">
                                                                    Contato
                                                                </label>
                                                                <div className="font-size-12">
                                                                    {log.recipientPhone && (
                                                                        <div><i className="mdi mdi-phone me-1 text-muted"></i>{log.recipientPhone}</div>
                                                                    )}
                                                                    {log.recipientEmail && (
                                                                        <div><i className="mdi mdi-email-outline me-1 text-muted"></i>{log.recipientEmail}</div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Timeline de status */}
                                                            <div className="col-md-6">
                                                                <label className="fw-bold font-size-11 text-muted text-uppercase mb-1 d-block">
                                                                    Timeline
                                                                </label>
                                                                <div className="font-size-12">
                                                                    {log.sentAt && (
                                                                        <div><i className="mdi mdi-send me-1 text-info"></i>Enviado: {formatDateTime(log.sentAt)}</div>
                                                                    )}
                                                                    {log.deliveredAt && (
                                                                        <div><i className="mdi mdi-check-all me-1 text-primary"></i>Entregue: {formatDateTime(log.deliveredAt)}</div>
                                                                    )}
                                                                    {log.readAt && (
                                                                        <div><i className="mdi mdi-eye-check me-1 text-success"></i>Lido: {formatDateTime(log.readAt)}</div>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Erro (se houver) */}
                                                            {log.status === "FAILED" && log.errorMessage && (
                                                                <div className="col-12">
                                                                    <div className="alert alert-danger mb-0 py-2 font-size-12">
                                                                        <i className="mdi mdi-alert-circle me-1"></i>
                                                                        <strong>{log.errorCode}: </strong>{log.errorMessage}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

/**
 * Mini card de estatística.
 */
const StatCard = ({ label, value, icon, color }) => (
    <div
        className="d-flex align-items-center gap-2 rounded-0 px-3 py-2 flex-fill"
        style={{ backgroundColor: color + '10', minWidth: '100px' }}
    >
        <i className={`mdi ${icon} font-size-18`} style={{ color }}></i>
        <div>
            <div className="fw-bold font-size-16" style={{ color, lineHeight: 1 }}>{value}</div>
            <div className="font-size-10 text-muted">{label}</div>
        </div>
    </div>
);

/**
 * Badge de canal (WhatsApp, Email, SMS, Push).
 */
const ChannelBadge = ({ channel }) => {
    const map = {
        whatsapp: { icon: "mdi-whatsapp", color: "#25D366", label: "WhatsApp" },
        email: { icon: "mdi-email-outline", color: "#0d6efd", label: "E-mail" },
        sms: { icon: "mdi-message-text-outline", color: "#fd7e14", label: "SMS" },
        push: { icon: "mdi-bell-outline", color: "#6f42c1", label: "Push" },
    };
    const info = map[channel] || map.whatsapp;
    return (
        <span className="d-inline-flex align-items-center gap-1 font-size-11">
            <i className={`mdi ${info.icon}`} style={{ color: info.color }}></i>
            {info.label}
        </span>
    );
};

/**
 * Badge de status do envio.
 */
const StatusBadge = ({ status }) => {
    const info = STATUS_MAP[status] || STATUS_MAP.QUEUED;
    return (
        <Badge color={info.color} className="d-inline-flex align-items-center gap-1 font-size-11 px-2 py-1 rounded-0">
            <i className={`mdi ${info.icon} font-size-12`}></i>
            {info.label}
        </Badge>
    );
};

export default AutomationLogModal;
